import { useFormControlContext } from '@chakra-ui/react'
import { ReactNode, useMemo } from 'react'
import ReactSelect, {
  components,
  InputProps,
  MultiValue,
  MultiValueRemoveProps,
  OptionProps,
  SingleValue
} from 'react-select'

import { buildSelectStyles, SelectSize } from './selectStyles'
import { ISelectOption, SelectValue } from './types'

interface ISearchableSelectBaseProps<T extends SelectValue> {
  options: ISelectOption<T>[]
  /** `Selecione` é o padrão. A biblioteca traz `Select...`, em inglês. */
  placeholder?: string
  /** `md` (padrão) é altura de campo de formulário; `sm` é a da barra de filtros. */
  size?: SelectSize
  isDisabled?: boolean
  /**
   * As opções ainda estão sendo buscadas: mostra o indicador, diz isso no lugar do vazio **e tranca
   * o campo** — sem as opções não há o que escolher, e um select `isClearable` aberto durante a
   * busca deixa apagar o valor já salvo.
   */
  isLoading?: boolean
  isClearable?: boolean
  /** Só quando não houver um `FormControl` em volta — de dentro dele o estado já chega sozinho. */
  isInvalid?: boolean
  /** Substantivo no singular e em minúsculas, para a frase de "nenhum resultado". */
  entity?: string
  /** Concorda a frase no feminino: `Nenhuma área encontrada`. */
  feminine?: boolean
  /** Escape para um vazio que não é "nenhum \<registro\>". */
  emptyMessage?: string
  /** `id` do input interno. Por padrão é o do `FormControl` em volta, para o rótulo focar o campo. */
  inputId?: string
  name?: string
  autoFocus?: boolean
  maxMenuHeight?: number
  /**
   * Escape para a linha do menu que não é só o rótulo — a opção com um botão de excluir ao lado,
   * por exemplo. A linha continua sendo desenhada pelo componente (fundo, foco, seleção): o que
   * entra aqui é o conteúdo dela, e a página não toca nos internos do react-select.
   */
  renderOption?: (option: ISelectOption<T>) => ReactNode
}

export type ISearchableSelectProps<T extends SelectValue> = ISearchableSelectBaseProps<T> &
  (
    | {
        isMulti: true
        value: T[]
        onChange: (value: T[]) => void
        /** Fecha o menu a cada escolha. No múltiplo o padrão é ficar aberto. */
        closeMenuOnSelect?: boolean
        emptyValue?: never
      }
    | {
        isMulti?: false
        value: T | ''
        onChange: (value: T | '') => void
        closeMenuOnSelect?: never
        /** O que o `onChange` devolve quando o campo é limpo. `''` por padrão. */
        emptyValue?: T | ''
      }
  )

/**
 * Select com busca do painel, único e múltiplo — o que fecha os três sistemas paralelos.
 *
 * O painel tinha o `<Select>` nativo do Chakra (40px), o `react-select` cru em 32 arquivos (38px,
 * importado de seis jeitos, com quatro `selectStyles` locais e 30 selects sem estilo nenhum) e um
 * `SearchableSelect` escrito à mão (32px, com portal, posicionamento e clique-fora próprios).
 * Formulários que misturavam os três ficavam com os campos desalinhados — o `UserCreateModal` tinha
 * os três empilhados.
 *
 * **O valor que entra e sai é o valor, não a opção.** Cada consumidor remontava o objeto
 * `{value,label}` a partir do estado (`options.find(...)`) e o desmontava de volta no `onChange`
 * (`option?.value ?? null`) — dez linhas por campo, e sempre um pouco diferentes.
 *
 * ```tsx
 * <FormField label="Curso" error={errors.course_id} isRequired>
 *   <SearchableSelect
 *     options={courses.map((course) => ({ value: course.id, label: course.name }))}
 *     value={form.course_id}
 *     onChange={(courseId) => setForm({ ...form, course_id: courseId })}
 *     entity="curso"
 *     isLoading={loadingCourses}
 *     isClearable
 *   />
 * </FormField>
 * ```
 *
 * - **O menu é portado para o `body`, sempre.** Oito selects passavam `menuPortalTarget` e 47 não;
 *   dentro de um `<FormModal>` — que rola por dentro desde a ABRA-283 — o menu não portado é
 *   recortado pelo corpo do modal ou rola junto com ele. Os 19 `zIndex: 9999` copiados nas páginas
 *   eram a tentativa de resolver isso por fora, e em 11 deles no alvo errado (`menu`, não
 *   `menuPortal`), que sem portal não muda nada.
 * - **As frases são em pt-BR.** A biblioteca responde `Select...`, `No options`, `Loading...`,
 *   `X results available` e `Remove <rótulo>` no botão de tirar um item do múltiplo, e só 6 dos 55
 *   selects traduziam alguma delas — os outros 49 mostravam inglês em tela assim que a lista ficava
 *   vazia ou a busca não casava. Ficam em inglês só as `ariaLiveMessages` (a narração genérica de
 *   combobox: `option X, selected`, `X results available, use Up and Down…`), que são da biblioteca
 *   e não falam do conteúdo desta tela.
 * - **Largura e altura não são prop.** A largura é 100% do que contém o campo (19 dos 23 sites do
 *   componente antigo digitavam `width="100%"` porque o padrão dele era `300px`), e a altura vem do
 *   `size`.
 * - **Dentro de um `FormField` o estado chega sozinho**: `isInvalid`, `isDisabled`, o `id`, o
 *   `aria-required` e o `aria-describedby` da mensagem de erro vêm do `FormControl`. Nenhum dos 55
 *   react-selects mostrava estado de erro, marcava obrigatoriedade ou anunciava a mensagem de
 *   validação — eles ficavam dentro de um `FormField`, mas não são controle do Chakra, então não
 *   recebiam nada disso. Os dois `aria-*` entram pelo input interno porque o react-select não tem
 *   prop para o primeiro e amarra o segundo à validação nativa.
 * - **A obrigatoriedade fica no `aria-required`, e não no `required` nativo.** A prop `required` do
 *   react-select monta um input escondido de validação; num modal com abas (`EventFormModal`) esse
 *   input fica dentro de um `TabPanel` com `hidden` quando a aba não é a ativa, e aí o Chrome se
 *   recusa a enviar o formulário **sem mostrar nada** — o botão Salvar simplesmente não faz nada.
 *   Medido: com "Tipo do Evento" vazio e a aba "FAQ" aberta, o clique em Salvar não dispara
 *   `submit` e o `reportValidity()` devolve `false`. A validação destes formulários já é em JS
 *   (`isConfirmDisabled`/`formValid`), então o ganho não paga o botão morto.
 */
export default function SearchableSelect<T extends SelectValue = string>(props: ISearchableSelectProps<T>) {
  const {
    options,
    placeholder = 'Selecione',
    size = 'md',
    isDisabled,
    isLoading = false,
    isClearable = false,
    isInvalid,
    entity,
    feminine = false,
    emptyMessage,
    inputId,
    name,
    autoFocus,
    maxMenuHeight,
    renderOption
  } = props

  // `strict: false` no contexto do Chakra: fora de um `FormControl` isto é `undefined`, embora o
  // tipo publicado não diga.
  const field = useFormControlContext() as ReturnType<typeof useFormControlContext> | undefined

  const invalid = isInvalid ?? field?.isInvalid ?? false
  // `isLoading` tranca o campo: sem as opções não há o que escolher, e um select `isClearable` aberto
  // durante a busca deixa apagar o valor já salvo. É a mesma regra do `FormSelect`.
  const disabled = isLoading || (isDisabled ?? field?.isDisabled ?? false)

  // O que o Chakra liga nos seus próprios controles e que o react-select não recebe sozinho: a
  // mensagem de erro (e o texto de apoio) para o leitor de tela, e a obrigatoriedade.
  const describedBy =
    [field?.hasFeedbackText && invalid ? field.feedbackId : null, field?.hasHelpText ? field.helpTextId : null]
      .filter(Boolean)
      .join(' ') || undefined
  const required = field?.isRequired ?? false

  const styles = useMemo(() => buildSelectStyles<T, boolean>(size, invalid), [size, invalid])

  const customComponents = useMemo(() => {
    const overrides: Partial<{
      Option: (optionProps: OptionProps<ISelectOption<T>, boolean>) => JSX.Element
      Input: (inputProps: InputProps<ISelectOption<T>, boolean>) => JSX.Element
      MultiValueRemove: (removeProps: MultiValueRemoveProps<ISelectOption<T>, boolean>) => JSX.Element
    }> = {}

    // O botão de tirar um item do múltiplo nasce com `aria-label="Remove <rótulo>"`, em inglês —
    // a mesma família do `No options` que o `noOptionsMessage` já resolve.
    const SelectMultiValueRemove = (removeProps: MultiValueRemoveProps<ISelectOption<T>, boolean>) => (
      <components.MultiValueRemove
        {...removeProps}
        innerProps={{ ...removeProps.innerProps, 'aria-label': `Remover ${removeProps.data.label}` }}
      />
    )
    SelectMultiValueRemove.displayName = 'SelectMultiValueRemove'
    overrides.MultiValueRemove = SelectMultiValueRemove

    if (renderOption) {
      const SelectOption = (optionProps: OptionProps<ISelectOption<T>, boolean>) => (
        <components.Option {...optionProps}>{renderOption(optionProps.data)}</components.Option>
      )
      SelectOption.displayName = 'SelectOption'
      overrides.Option = SelectOption
    }

    // O react-select não tem prop para `aria-describedby`, e a `required` dele — que também
    // desenharia o `aria-required` — traz junto um input escondido de validação nativa. Os dois
    // entram pelo input interno. O `aria-describedby` **soma** ao da biblioteca (ela aponta para o
    // próprio placeholder) em vez de substituí-lo.
    if (describedBy || required) {
      const SelectInput = (inputProps: InputProps<ISelectOption<T>, boolean>) => (
        <components.Input
          {...inputProps}
          aria-describedby={[inputProps['aria-describedby'], describedBy].filter(Boolean).join(' ') || undefined}
          aria-required={required || undefined}
        />
      )
      SelectInput.displayName = 'SelectInput'
      overrides.Input = SelectInput
    }

    return overrides
  }, [renderOption, describedBy, required])

  const noOptions =
    emptyMessage ??
    (entity ? `Nenhum${feminine ? 'a' : ''} ${entity} encontrad${feminine ? 'a' : 'o'}` : 'Nenhum resultado')

  const selected = props.isMulti
    ? options.filter((option) => props.value.includes(option.value))
    : (options.find((option) => option.value === props.value) ?? null)

  const handleChange = (option: MultiValue<ISelectOption<T>> | SingleValue<ISelectOption<T>>) => {
    if (props.isMulti) {
      props.onChange((option as MultiValue<ISelectOption<T>>).map((item) => item.value))
      return
    }

    props.onChange((option as SingleValue<ISelectOption<T>>)?.value ?? props.emptyValue ?? '')
  }

  return (
    <ReactSelect<ISelectOption<T>, boolean>
      options={options}
      value={selected}
      onChange={handleChange}
      isMulti={props.isMulti}
      closeMenuOnSelect={props.isMulti ? (props.closeMenuOnSelect ?? false) : true}
      placeholder={placeholder}
      isDisabled={disabled}
      isLoading={isLoading}
      isClearable={isClearable}
      isOptionDisabled={(option: ISelectOption<T>) => Boolean(option.isDisabled)}
      inputId={inputId ?? field?.id}
      aria-invalid={invalid || undefined}
      name={name}
      autoFocus={autoFocus}
      maxMenuHeight={maxMenuHeight}
      // O menu vive no `body` para escapar do corpo rolante do modal e de qualquer `overflow` no meio.
      menuPortalTarget={typeof document === 'undefined' ? undefined : document.body}
      menuPosition="fixed"
      components={customComponents}
      noOptionsMessage={() => noOptions}
      loadingMessage={() => 'Carregando...'}
      screenReaderStatus={({ count }: { count: number }) =>
        `${count} ${count === 1 ? 'resultado disponível' : 'resultados disponíveis'}`
      }
      styles={styles}
    />
  )
}
